import React, { useState } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { billingService } from 'services/billingService';
import { usePatients } from 'hooks/usePatients';
import { useIssueInvoice } from 'hooks/useInvoices';
import { IssueTaxInfoDto, Patient, PaymentReceipt, ReceiptStatus } from 'types/models';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const EMPTY_TAX: IssueTaxInfoDto = { businessName: '', rucOrId: '', direccion: '', email: '' };

export const IssueInvoiceModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const [step, setStep]                   = useState(1);
  const [patientId, setPatientId]         = useState('');
  const [selected, setSelected]           = useState<string[]>([]);
  const [taxInfo, setTaxInfo]             = useState<IssueTaxInfoDto>(EMPTY_TAX);

  const patients: Patient[] = usePatients().data ?? [];

  const { data: receipts = [], isLoading: receiptsLoading } = useQuery({
    queryKey: ['billing-receipts', patientId],
    queryFn:  () => billingService.getReceipts(patientId),
    enabled:  !!patientId,
  });

  const pending = receipts.filter(r => r.receiptStatus === ReceiptStatus.PENDIENTE);

  const { mutate: issueInvoice, isPending, error } = useIssueInvoice();

  const totalAmount = pending
    .filter(r => selected.includes(r.id))
    .reduce((sum, r) => sum + Number(r.amount), 0);

  const taxValid =
    taxInfo.businessName.trim() &&
    taxInfo.rucOrId.trim() &&
    taxInfo.direccion.trim() &&
    taxInfo.email.trim();

  const canSubmit = selected.length > 0 && !!taxValid;

  const handlePatient = (id: string) => {
    setPatientId(id);
    setSelected([]);
  };

  const toggle = (id: string) =>
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const handleClose = () => {
    setStep(1);
    setPatientId('');
    setSelected([]);
    setTaxInfo(EMPTY_TAX);
    onClose();
  };

  const handleSubmit = () => {
    issueInvoice(
      { patientId, dto: { receiptIds: selected, taxInfo } },
      { onSuccess: handleClose },
    );
  };

  const summaryBg = useColorModeValue('blue.50', 'blue.900');
  const summaryColor = useColorModeValue('blue.700', 'blue.200');

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          Emitir Factura Electrónica
          <Text fontSize="sm" fontWeight="normal" color="gray.500" mt={0.5}>
            Paso {step} de 2 — {step === 1 ? 'Seleccionar recibos' : 'Datos fiscales'}
          </Text>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          {step === 1 ? (
            <VStack align="stretch" spacing={4}>
              <FormControl isRequired>
                <FormLabel>Paciente</FormLabel>
                <Select
                  placeholder="Seleccionar paciente..."
                  value={patientId}
                  onChange={e => handlePatient(e.target.value)}
                >
                  {patients.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.apellidos} {p.nombres} — {p.cedula}
                    </option>
                  ))}
                </Select>
              </FormControl>

              {patientId && (
                <>
                  <Divider />
                  <Text fontWeight="semibold" fontSize="sm">
                    Recibos PENDIENTE de facturar
                  </Text>

                  {receiptsLoading ? (
                    <HStack justify="center" py={4}><Spinner size="sm" /></HStack>
                  ) : pending.length === 0 ? (
                    <Alert status="info" borderRadius="md">
                      <AlertIcon />
                      Sin recibos pendientes para este paciente.
                    </Alert>
                  ) : (
                    <VStack align="stretch" spacing={2} maxH="260px" overflowY="auto">
                      {pending.map(r => (
                        <ReceiptRow
                          key={r.id}
                          receipt={r}
                          checked={selected.includes(r.id)}
                          onToggle={() => toggle(r.id)}
                        />
                      ))}
                    </VStack>
                  )}

                  {selected.length > 0 && (
                    <HStack
                      justify="space-between"
                      bg={summaryBg}
                      px={3}
                      py={2}
                      rounded="md"
                    >
                      <Text fontSize="sm" color={summaryColor}>
                        {selected.length} recibo{selected.length !== 1 ? 's' : ''} seleccionado{selected.length !== 1 ? 's' : ''}
                      </Text>
                      <Text fontWeight="bold" color={summaryColor}>
                        ${totalAmount.toFixed(2)}
                      </Text>
                    </HStack>
                  )}
                </>
              )}
            </VStack>
          ) : (
            <VStack align="stretch" spacing={4}>
              <FormControl isRequired>
                <FormLabel>Razón Social</FormLabel>
                <Input
                  value={taxInfo.businessName}
                  onChange={e => setTaxInfo(p => ({ ...p, businessName: e.target.value }))}
                  placeholder="Nombre o razón social del receptor"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>RUC / Cédula</FormLabel>
                <Input
                  value={taxInfo.rucOrId}
                  onChange={e => setTaxInfo(p => ({ ...p, rucOrId: e.target.value }))}
                  placeholder="Ej: 1722334455001"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Dirección Fiscal</FormLabel>
                <Input
                  value={taxInfo.direccion}
                  onChange={e => setTaxInfo(p => ({ ...p, direccion: e.target.value }))}
                  placeholder="Dirección del receptor"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Correo Electrónico</FormLabel>
                <Input
                  type="email"
                  value={taxInfo.email}
                  onChange={e => setTaxInfo(p => ({ ...p, email: e.target.value }))}
                  placeholder="correo@empresa.com"
                />
              </FormControl>

              <Box bg={summaryBg} px={3} py={2} rounded="md">
                <Text fontSize="sm" color={summaryColor}>
                  Total a facturar:{' '}
                  <strong>${totalAmount.toFixed(2)}</strong>{' '}
                  ({selected.length} recibo{selected.length !== 1 ? 's' : ''})
                </Text>
              </Box>

              {error && (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  {(error as any)?.response?.data?.message ?? 'Error al emitir la factura'}
                </Alert>
              )}
            </VStack>
          )}
        </ModalBody>

        <ModalFooter gap={2}>
          {step === 1 ? (
            <>
              <Button variant="ghost" onClick={handleClose}>Cancelar</Button>
              <Button
                colorScheme="blue"
                isDisabled={selected.length === 0}
                onClick={() => setStep(2)}
              >
                Continuar
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setStep(1)}>Atrás</Button>
              <Button
                colorScheme="green"
                isDisabled={!canSubmit}
                isLoading={isPending}
                onClick={handleSubmit}
              >
                Emitir Factura
              </Button>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const ReceiptRow: React.FC<{
  receipt: PaymentReceipt;
  checked: boolean;
  onToggle: () => void;
}> = ({ receipt, checked, onToggle }) => {
  const bg = useColorModeValue('gray.50', 'gray.700');

  return (
    <HStack
      px={3}
      py={2}
      bg={bg}
      rounded="md"
      cursor="pointer"
      justify="space-between"
      onClick={onToggle}
      _hover={{ opacity: 0.8 }}
    >
      <HStack>
        <Checkbox
          isChecked={checked}
          onChange={onToggle}
          onClick={e => e.stopPropagation()}
        />
        <Box>
          <Text fontSize="sm" fontWeight="medium">{receipt.receiptNumber}</Text>
          <Text fontSize="xs" color="gray.500">
            {new Date(receipt.paidAt).toLocaleDateString('es-EC')} · {receipt.paymentMethod.type}
          </Text>
        </Box>
      </HStack>
      <Text fontWeight="semibold" fontSize="sm">
        ${Number(receipt.amount).toFixed(2)}
      </Text>
    </HStack>
  );
};
